/**
 * useAuth Hook
 *
 * Main authentication hook providing access to auth state and actions
 * Integrates with NextAuth session
 */

import { useCallback } from 'react';
import { useSelector } from '@legendapp/state/react';
import { authStore$, clearAuthData } from '../stores/auth.store';
import type {
    CustomerToken,
    CustomerProfile,
    AuthenticationStep,
    AuthMode,
    InitValidatePhoneParams,
    ValidatePhoneType,
} from '../types';

/**
 * useAuth hook return type
 */
export interface UseAuthReturn {
    // State
    isAuthenticated: boolean;
    isLoading: boolean;
    customerToken: CustomerToken | undefined;
    customerProfile: CustomerProfile | undefined;
    error: string | undefined;
    currentStep: AuthenticationStep;
    authMode: AuthMode;
    initValidatePhoneData?: InitValidatePhoneParams;
    validatePhoneType?: ValidatePhoneType;

    // Actions
    clearAuth: () => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | undefined) => void;
    setCurrentStep: (newStep: AuthenticationStep) => void;
    setAuthMode: (mode: AuthMode) => void;
    setValidatePhoneType: (type: ValidatePhoneType) => void;
}

/**
 * useAuth hook
 *
 * Provides reactive access to authentication state
 * Use this hook in components that need auth information
 *
 * @example
 * ```tsx
 * const { isAuthenticated, customerProfile, clearAuth } = useAuth();
 *
 * if (!isAuthenticated) {
 *   return <LoginPrompt />;
 * }
 *
 * return <UserDashboard user={customerProfile} onLogout={clearAuth} />;
 * ```
 */
export const useAuth = (): UseAuthReturn => {
    // Subscribe to auth store changes using useSelector
    const isAuthenticated = useSelector(() => authStore$.isAuthenticated.get() ?? false);
    const isLoading = useSelector(() => authStore$.isLoading.get() ?? false);
    const customerToken = useSelector(() => authStore$.customerToken.get());
    const customerProfile = useSelector(() => authStore$.customerProfile.get());
    const error = useSelector(() => authStore$.error.get());
    const currentStep = useSelector(() => authStore$.step.get() ?? 'EnterPhoneNumber');
    const authMode = useSelector(() => authStore$.authMode.get() ?? 'login');
    const initValidatePhoneData = useSelector(() => authStore$.initValidatePhoneData.get());
    const validatePhoneType = useSelector(() => authStore$.validatePhoneType.get());

    // Actions
    const clearAuth = useCallback(() => {
        clearAuthData();
    }, []);

    const setLoading = useCallback((loading: boolean) => {
        authStore$.isLoading.set(loading);
    }, []);

    const setError = useCallback((newError: string | undefined) => {
        authStore$.error.set(newError);
    }, []);

    const setCurrentStep = useCallback((newStep: AuthenticationStep) => {
        authStore$.step.set(newStep);
    }, []);

    const setAuthMode = useCallback((mode: AuthMode) => {
        authStore$.authMode.set(mode);
    }, []);

    const setValidatePhoneType = useCallback((type: ValidatePhoneType) => {
        authStore$.validatePhoneType.set(type);
    }, []);

    return {
        initValidatePhoneData,
        isAuthenticated,
        isLoading,
        customerToken,
        customerProfile,
        error,
        currentStep,
        authMode,
        validatePhoneType,
        setValidatePhoneType,
        clearAuth,
        setLoading,
        setError,
        setCurrentStep,
        setAuthMode,
    };
};
